import { FCX, useWindowSizeDebounced, getScaledPixels, getRem } from "@datassential/platform-ui-lib";
import TableHead from "@components/Table/TableHead";
import { FixedSizeList } from 'react-window';
import TableRow from "@components/Table/TableRow";
import { AutoSizer } from "react-virtualized";
import cn from "classnames";
import React, { memo, useMemo } from "react";

const VirtualizedTable: FCX<{
    isLoading?: boolean;
    headHeight?: number;
    rowHeight?: number;
    rowsCount: number;
    stickyRowsCount?: number;
    contentWidthPx?: number;
    useContentHeight?: boolean;
    onRowHover?: (index: number | null) => void;
    showFallback?: boolean;
}> = ({
    isLoading,
    headHeight = 38,
    rowHeight = 38,
    rowsCount,
    stickyRowsCount = 0,
    style,
    children: tableChildren,
    testId,
    className,
    contentWidthPx,
    onRowHover,
    useContentHeight = false,
    showFallback,
}) => {
    const [wWidth] = useWindowSizeDebounced();

    // Calculate the total content height
    const contentHeight = useMemo(() => {
        const scaledHeadHeight = getScaledPixels(headHeight, wWidth);
        const scaledRowHeight = getScaledPixels(rowHeight, wWidth);
        const totalRowsHeight = (rowsCount * scaledRowHeight);
        return scaledHeadHeight + totalRowsHeight;
    }, [headHeight, rowHeight, rowsCount, wWidth]);

    const defaultFallback = useMemo(
        () => {
            if (rowsCount === 0 && !isLoading && showFallback) {
                return (
                    <span className="Table__fallback">No matching items found</span>
                );
            }

            return null;
        },
        [rowsCount, isLoading, showFallback]
    );

    return (
        <div
            className={
                cn("Table", className)
            }
            style={{
                height: '100%',
                ...style,
            }}
            data-testid={testId}
        >
            <div style={{ flex: 1, position: 'relative', maxHeight: useContentHeight ? contentHeight : undefined }}>
                <AutoSizer>
                    {({ width, height }) => {
                        // Use content height if it's smaller than AutoSizer height, otherwise use AutoSizer height
                        const effectiveHeight = Math.min(contentHeight, height);

                        return (
                            <div style={{ position: 'relative', width, height: effectiveHeight }}>
                                <FixedSizeList
                                    itemSize={getScaledPixels(rowHeight, wWidth)}
                                    width={width}
                                    height={effectiveHeight}
                                    itemCount={rowsCount - stickyRowsCount}
                                    outerElementType={React.forwardRef<HTMLDivElement, { children: React.ReactNode; headHeight: number; wWidth: number }>(
                                        ({ children, ...props }, ref) => {
                                            const scaledHeadHeight = getScaledPixels(headHeight, wWidth)
                                            const scaledStickyRowHeight = getScaledPixels(rowHeight, wWidth)
                                            return (
                                                <div {...props} ref={ref}>
                                                    <div style={{ position: 'sticky', top: 0, zIndex: 2, height: scaledHeadHeight }}>
                                                        <TableHead
                                                            height={headHeight}
                                                            style={{
                                                                minWidth: contentWidthPx ? getRem(contentWidthPx) : undefined,
                                                            }}
                                                        />
                                                    </div>
                                                    {stickyRowsCount > 0 && (
                                                        Array(stickyRowsCount).fill(0).map((_, index) => (
                                                            <div
                                                                key={`stickyRow${index}`}
                                                                style={{
                                                                    position: 'sticky',
                                                                    top: (scaledHeadHeight + (index * scaledStickyRowHeight)),
                                                                    zIndex: 1,
                                                                    height: scaledStickyRowHeight
                                                                }}
                                                            >
                                                                <TableRow
                                                                    isSticky
                                                                    index={index}
                                                                    isLoading={isLoading}
                                                                    style={{
                                                                        minWidth: contentWidthPx ? getRem(contentWidthPx) : undefined,
                                                                    }}
                                                                />
                                                            </div>
                                                        ))
                                                    )}
                                                    {children}
                                                </div>
                                            )
                                        }
                                    )}
                                >
                                    {({ index, style }) => (
                                        <TableRow 
                                            index={index + stickyRowsCount}
                                            isLoading={isLoading}
                                            onHover={onRowHover}
                                            style={{
                                                ...style,
                                                top: (style.top as number) + getScaledPixels(headHeight, wWidth) + (stickyRowsCount * getScaledPixels(rowHeight, wWidth)),
                                                minWidth: contentWidthPx ? getRem(contentWidthPx) : undefined,
                                            }}
                                        />
                                    )}
                                </FixedSizeList>
                                {tableChildren}
                                {defaultFallback}
                            </div>
                        );
                    }}
                </AutoSizer>
            </div>
        </div>
    );
}

export default memo(VirtualizedTable);